import {all, call, fork, put, race, select, take, takeLatest} from "redux-saga/effects";
import {ActionType, getType} from "typesafe-actions";
import {loadScene} from "./actions";
import {AppScene} from "./AppScene";
import {fetchCategories, fetchLevelMap, fetchQuestions} from "../game/actions";
import {IApplicationState} from "../index";
import {errorMessageFor, loadingStatusFor} from "../common/selectors";
import {ActionCreator} from "typesafe-actions/dist/type-helpers";
import {delay} from "redux-saga";
import {closeAds, watchAds} from "../advertisement/actions";
import {endMetric, startMetric} from "../log/performance";
import {trackError} from "../log/actions";
import {LogLevel} from "../log/types";
import {gameInit} from "../common/actions";
import forkWithErrHandler from "../../shared/utils/forkWithErrHandler";
import handleEntryPoint from "../game/sagas/handleEntryPoint";

function* resolveSceneDependencies (asyncActions: Array<{
    request: ActionCreator<string>;
    success: ActionCreator<string>;
    failure: ActionCreator<string>;
}>) {
    while (true) {
        const {loading, errors} = yield select(({ loading, log }: IApplicationState) => ({
            loading: loading,
            errors: log,
        }));

        const request = asyncActions.map(action => getType(action.request));

        const isError = errorMessageFor(errors, request);
        if (isError) {
            return false;
        }

        const isLoading = loadingStatusFor(loading, request);
        if (!isLoading) {
            return true;
        }

        const {resolved, timeout} = yield race({
            resolved: all(asyncActions.map(action => take(getType(action.success)))),
            unresolved: race(asyncActions.reduce((effects, action) => ({
                ...effects,
                [getType(action.request)]: take(getType(action.failure))
            }), {})),
            timeout: delay(300)
        });

        if (timeout) {
            continue;
        }

        return !!resolved;
    }
}

function getDependencies(scene: AppScene) {
    switch (scene) {
        case "LevelMap":
            return [fetchCategories, fetchLevelMap];

        case "Question":
            return [fetchQuestions];

        default:
            return [];
    }
}

function* waitForWatchingAds(scene: AppScene) {
    if (scene !== "LevelMap" && scene !== "LevelSelection")
        return;

    const {previousScene, interstitialAdIsAvailable} = yield select(({ routing, advertisement }: IApplicationState) => ({
        previousScene: routing.scene,
        interstitialAdIsAvailable: advertisement.interstitialAdIsAvailable
    }));

    if (previousScene !== "Question" || !interstitialAdIsAvailable)
        return;

    const metricId = yield call(startMetric, "waitForAds", scene);

    try {
        yield put(watchAds({
            adType: 'interstitial'
        }));

        yield take(getType(closeAds));
    }
    catch (error) {
        yield put(trackError({
            logLevel: LogLevel.Fatal,
            category: "RoutingSaga",
            message: "Failed to waiting for ads",
            error: error,
            data: {
                scene,
            }
        }));
    }
    finally {
        yield fork(endMetric, metricId);
    }
}

function* watchLoadScene() {
    const watchingAdsTimeout = yield select(({ settings }: IApplicationState) => settings.advertisement.loadSceneTimeout);

    yield takeLatest(getType(loadScene.request), function* ({ payload: scene }: ActionType<typeof loadScene>) {
        const metricId = yield call(startMetric, "loadScene", scene);

        let attempt = 0;

        while (true) {
            const resolved = yield call(resolveSceneDependencies, getDependencies(scene));

            if (resolved) {
                const {timeout} = yield race({
                    watchAds: call(waitForWatchingAds, scene),
                    timeout: delay(watchingAdsTimeout)
                });

                if (timeout) {
                    yield put(trackError({
                        logLevel: LogLevel.Error,
                        category: "RoutingSaga",
                        message: "Interstitial ad has been canceled",
                        data: {
                            scene,
                        }
                    }));
                }

                yield put(loadScene.success(scene));

                yield fork(endMetric, metricId);
                break;
            } else {
                const {loadSceneRetryTimeout, loadSceneRetryCount} = yield select(({ settings }: IApplicationState) => settings.general);

                if (attempt > loadSceneRetryCount) {
                    yield put(loadScene.failure(scene));

                    yield put(trackError({
                        logLevel: LogLevel.Fatal,
                        category: "RoutingSaga",
                        message: "Failed to load scene",
                        data: {
                            scene,
                        }
                    }));

                    break;
                }

                attempt++;

                yield delay(loadSceneRetryTimeout);
            }
        }
    });
}

function* loadDefaultScene() {
    const {payload: entryPoint} = yield take(gameInit);

    if (entryPoint) {
        yield handleEntryPoint(entryPoint);
    } else {
        const {scene} = yield select(({ routing }: IApplicationState) => ({
            scene: routing.scene,
        }));

        if (!scene) {
            yield put(loadScene.request('MainMenu'));
        }
    }
}

export default function* routerSaga() {
    yield all([
        forkWithErrHandler("routerSaga", "loadDefaultScene", loadDefaultScene),
        forkWithErrHandler("routerSaga", "watchLoadScene", watchLoadScene),
    ]);
}